import { useLayoutEffect } from 'react';

import { useAccountStore } from '../stores/accountStore';
import { useStoreSelectionStore } from '../stores/storeSelectionStore';
import { useUIStore } from '../stores/uiStore';

export const ZUSTAND_USER_KEY = 'zeroth-zustand-user';
export const ZUSTAND_STATE_KEYS = [
  'zeroth-account',
  'zeroth-store-selection',
  'zeroth-ui-state',
] as const;

function getBrowserStorage(): Storage | undefined {
  return typeof window === 'undefined' ? undefined : window.localStorage;
}

export function clearPersistedZustandState(
  storage: Storage | undefined = getBrowserStorage()
): void {
  useAccountStore.getState().clearAccount();
  useStoreSelectionStore.getState().reset();
  useUIStore.getState().reset();

  for (const key of ZUSTAND_STATE_KEYS) {
    try {
      storage?.removeItem(key);
    } catch {
      return;
    }
  }
}

export function syncZustandUserMarker(
  userId: string | null | undefined,
  storage: Storage | undefined = getBrowserStorage()
): boolean {
  if (!storage) return false;

  const nextUserId = userId ?? null;
  let previousUserId: string | null;
  try {
    previousUserId = storage.getItem(ZUSTAND_USER_KEY);
  } catch {
    return false;
  }

  if (previousUserId === nextUserId) {
    return false;
  }

  clearPersistedZustandState(storage);

  try {
    if (nextUserId) {
      storage.setItem(ZUSTAND_USER_KEY, nextUserId);
    } else {
      storage.removeItem(ZUSTAND_USER_KEY);
    }
  } catch {
    // State has already been reset in memory; marker persistence is best effort.
  }

  return true;
}

export function useZustandUserIsolation(
  userId: string | null | undefined,
  isAuthResolved: boolean
): void {
  useLayoutEffect(() => {
    if (!isAuthResolved) return;
    syncZustandUserMarker(userId);
  }, [isAuthResolved, userId]);
}
